"use client";

import type { ActivityPeriod } from "@/domain/gems-game/domain/lib/Sweepstake";
import { calculateMythicPercentage } from "@/utils/rewards";
import { trackLegacyEvent } from "@analytics";
import { Body, Box, BulletList, Divider, Heading, Stack } from "@biom3/react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { type PrizeConfig, useWeeklyRewards } from "../WeeklyRewardsProvider";
import { OverallPrizeTypes } from "../tiers/TiersContent";

const FaqHeading = ({ children }: { children: React.ReactNode }) => (
  <Body
    weight="bold"
    size="medium"
    sx={{
      color: "base.color.text.body.primary",
    }}
  >
    {children}
  </Body>
);

const FaqBody = ({ children }: { children: React.ReactNode }) => (
  <Body size="medium" sx={{ color: "base.color.text.body.secondary" }}>
    {children}
  </Body>
);

const SectionHeading = ({
  children,
  isExpanded,
  onClick,
  sectionId,
}: {
  children: React.ReactNode;
  isExpanded: boolean;
  onClick: () => void;
  sectionId: string;
}) => {
  const handleClick = () => {
    // Track section expansion/collapse
    trackLegacyEvent({
      screen: "FAQ",
      userJourney: "Rewards",
      action: isExpanded ? "Collapsed" : "Expanded",
      control: `Section_${sectionId}`,
      controlType: "Accordion",
      extras: {
        sectionName: children,
      },
    });

    onClick();
  };

  return (
    <Box
      id={sectionId}
      onClick={handleClick}
      sx={{
        borderBottom: "1px solid",
        borderColor: "base.color.translucent.emphasis.100",
        mt: "base.spacing.x4",
        mb: "base.spacing.x4",
        pb: "base.spacing.x2",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Heading
        sx={{
          fontSize: "24px",
          color: "base.color.text.heading.primary",
        }}
      >
        {children}
      </Heading>
      <Body size="large">{isExpanded ? "−" : "+"}</Body>
    </Box>
  );
};

const FaqItem = ({
  question,
  children,
  number,
}: {
  question: React.ReactNode;
  children: React.ReactNode;
  number: number;
}) => (
  <Box sx={{ mt: "base.spacing.x4" }}>
    <Stack direction="column" gap="base.spacing.x4">
      <FaqHeading>
        {number}. {question}
      </FaqHeading>
      {children}
    </Stack>
  </Box>
);

const VALID_SECTIONS = [
  "weekly-rewards-program",
  "eligibility-earning-gems",
  "redeeming-gems-weekly-draw",
  "key-types-tiers",
  "claiming-understanding-rewards",
  "verification-anti-fraud",
  "identity-verification",
  "phone-verification-errors",
  "social-linking-errors",
  "managing-linked-accounts",
  "getting-help",
];

const SparkSweepstakeFAQContent = ({
  prizeConfig,
  activityPeriod,
  mythicPercentage,
  expandedSections,
  toggleSection,
}: {
  prizeConfig: PrizeConfig;
  activityPeriod: ActivityPeriod | undefined;
  mythicPercentage: number;
  expandedSections: Record<string, boolean>;
  toggleSection: (section: string) => void;
}) => {
  return (
    <Stack direction="column">
      <SectionHeading
        isExpanded={expandedSections["weekly-rewards-program"]}
        onClick={() => toggleSection("weekly-rewards-program")}
        sectionId="weekly-rewards-program"
      >
        Introducing the Weekly Rewards Program
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["weekly-rewards-program"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["weekly-rewards-program"] ? "none" : "0",
          overflow: expandedSections["weekly-rewards-program"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="What is the Weekly Draw in the Perpetual Rewards Program?"
        >
          <FaqBody>
            The Weekly Draw allows eligible users to redeem 500 Gems in exchange
            for a Key. This Key grants entry to a weekly rewards draw offering
            IMX tokens, bonus Gems, and free draw entries.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={2}
          question="How do I increase my Key tier and earn more rewards?"
        >
          <FaqBody>
            Complete more eligible quests to earn <strong>Weekly Points</strong>{" "}
            and increase your Key tier until the end of the week (
            <strong>Thursday to Thursday UTC</strong>). Click on the progress
            bar at the top of the screen to reveal the complete list of eligible
            quests.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={3}
          question="What is the schedule for the Weekly Draw?"
        >
          <Stack direction="column" gap="base.spacing.x4">
            <Stack direction="column" gap="base.spacing.x2">
              <FaqBody>
                Each Weekly Draw runs from{" "}
                <strong>Thursday to Thursday (UTC).</strong>
              </FaqBody>
              <FaqBody>
                <BulletList
                  use={<ul />}
                  sx={{
                    "& li": {
                      color: "base.color.text.body.secondary",
                    },
                  }}
                >
                  <BulletList.Item size="small">
                    <strong>Claim a Key:</strong> Redeem 500 Gems for a Key at
                    any time during the week.
                  </BulletList.Item>
                  <BulletList.Item size="small">
                    <strong>Earn Weekly Points:</strong> Complete eligible
                    quests to earn Weekly Points and increase your Key tier in
                    real time throughout the week.
                  </BulletList.Item>
                  <BulletList.Item size="small">
                    <strong>Unlock Rewards:</strong> At{" "}
                    <strong>1am UTC every Thursday, </strong>
                    rewards from the previous week become available to unlock.
                  </BulletList.Item>
                </BulletList>
              </FaqBody>
            </Stack>
          </Stack>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["eligibility-earning-gems"]}
        onClick={() => toggleSection("eligibility-earning-gems")}
        sectionId="eligibility-earning-gems"
      >
        Eligibility, Participation &amp; Earning Gems
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["eligibility-earning-gems"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["eligibility-earning-gems"]
            ? "none"
            : "0",
          overflow: expandedSections["eligibility-earning-gems"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="What do I need to do to qualify for weekly rewards?"
        >
          <FaqBody>Anyone with enough Gems can enter a Weekly Draw.</FaqBody>
        </FaqItem>

        <FaqItem number={2} question="How do I qualify for IMX rewards?">
          <FaqBody>
            In order to qualify for IMX rewards, you will be required to verify
            your account by linking both a phone number and a social media
            account.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={3}
          question="Which quests should I complete to earn Weekly Points and increase my Key tier?"
        >
          <FaqBody>
            You can see the complete list of eligible quests by clicking on the
            progress bar at the top of the screen.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={4}
          question="Are there additional rewards for completing more than one quest?"
        >
          <FaqBody>
            Yes. You can unlock even greater rewards by completing multiple
            eligible quests, claiming your daily Gems, and further engaging with
            games.
          </FaqBody>
        </FaqItem>

        <FaqItem number={5} question="How can I earn more gems?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              You can earn Gems by completing various activities across
              Immutable Play, including:
            </FaqBody>
            <FaqBody>
              <BulletList
                use={<ul />}
                sx={{
                  "& li": {
                    color: "base.color.text.body.secondary",
                  },
                }}
              >
                <BulletList.Item size="small">
                  Completing eligible quests from supported games
                </BulletList.Item>
                <BulletList.Item size="small">Watching videos</BulletList.Item>
                <BulletList.Item size="small">
                  Following and interacting with supported games
                </BulletList.Item>
                <BulletList.Item size="small">
                  Participating in limited-time campaigns or promotions
                </BulletList.Item>
              </BulletList>
            </FaqBody>
            <FaqBody>
              New Gem-earning opportunities are added regularly, so check the
              Rewards and Quests page often to stay current.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem
          number={6}
          question="Do I need an Immutable Passport wallet to participate?"
        >
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              Yes. The Weekly Rewards program is only available to users of
              Immutable Passport. Previously, users with externally owned
              account (EOA) wallets (e.g., MetaMask) could collect daily Gems
              and complete eligible quests, but those wallets are not eligible
              for the Weekly Rewards program.
            </FaqBody>
            <FaqBody>
              A one-time migration option will allow each EOA wallet holder to
              transfer their Gems to an Immutable Passport wallet in the coming
              weeks. Once migrated, you can retain your gems and progress and
              continue participating using your Passport wallet.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem
          number={7}
          question="Can I participate in the Weekly Rewards program with an EOA wallet?"
        >
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              <b>Not yet - but we&apos;re working on it.</b> Currently, only
              Immutable Passport wallets can participate in the Weekly Rewards
              program. EOA wallets (like MetaMask) aren&apos;t supported for
              redeeming Gems or entering the Weekly Draw.
            </FaqBody>
            <FaqBody>
              However, you&apos;re not missing out if you&apos;ve used an EOA
              wallet to complete quests and collect Gems. A one-time migration
              option will allow you to transfer your Gems and progress to a
              Passport wallet in the coming weeks.
            </FaqBody>
            <FaqBody>
              Once that&apos;s available, you can redeem your existing Gems and
              join future Weekly Draws - just like everyone else. Rewards are
              ongoing, so you&apos;ll still be able to get value from the Gems
              you&apos;ve already earned.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem
          number={8}
          question="Can I participate in the Perpetual Rewards Program from all countries?"
        >
          <FaqBody>
            Immutable Play uses the Immutable Passport for authentication. For
            details on supported countries, please refer to the{" "}
            <Link
              href="https://support.immutable.com/hc/en-us/articles/9398903265551-What-regions-does-Passport-support"
              target="_blank"
              rel="noopener noreferrer"
            >
              Immutable Passport support website
            </Link>
            .
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={9}
          question="What other criteria apply for participating in the Perpetual Rewards Program?"
        >
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              <Link
                href="https://www.immutable.com/perpetual-rewards-terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span style={{ fontWeight: "bold" }}>
                  Terms and Conditions apply.
                </span>
              </Link>{" "}
              No purchase is necessary to enter or to receive a reward. A
              purchase does not increase the chance of receiving a reward. The
              promotion is open worldwide to eligible entrants aged 18+. Where
              prohibited by law in a country or region, this promotion is void
              and not available to residents of that country or region; such
              residents are ineligible, and rewards cannot be received.
            </FaqBody>
            <FaqBody>
              This promotion is sponsored and promoted by the IMX Ecosystem
              Foundation and operated by Immutable Play.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem number={10} question="What if I don't have enough Gems?">
          <FaqBody>
            You can earn more by engaging with Immutable Play - e.g., completing
            quests, claiming daily Gems, watching videos, or following games.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["redeeming-gems-weekly-draw"]}
        onClick={() => toggleSection("redeeming-gems-weekly-draw")}
        sectionId="redeeming-gems-weekly-draw"
      >
        Redeeming Gems &amp; Weekly Draw Entry
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["redeeming-gems-weekly-draw"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["redeeming-gems-weekly-draw"]
            ? "none"
            : "0",
          overflow: expandedSections["redeeming-gems-weekly-draw"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem number={1} question="How do Gems and Keys work?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              You earn Gems by engaging with Immutable Play. Once you collect
              500 Gems, you can redeem Gems for a key to enter the current
              week&apos;s Weekly Draw.
            </FaqBody>
            <FaqBody>
              You may claim one key per week (
              <strong>Thursday to Thursday UTC</strong>). Your Key&apos;s tier
              will increase in real-time as you complete more quests throughout
              the week.
            </FaqBody>
            <FaqBody>
              Each Key is valid only for that specific Weekly Draw. After the
              draw is finished, your rewards become available to unlock on{" "}
              <strong>Thursday 1am UTC</strong>. If you don&apos;t unlock them
              within <strong>30 days</strong>, they will expire.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem number={2} question="How do I enter the Weekly Draw?">
          <FaqBody>
            To enter the Weekly Draw, redeem <strong>500 Gems</strong> during
            any time during the week (<strong>Thursday to Thursday UTC</strong>)
            in exchange for a Key. Once redeemed, your Key automatically enters
            you into the Weekly Draw for that cycle.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={3}
          question="How many Gems do I need to enter the Weekly Draw?"
        >
          <FaqBody>
            You need 500 Gems to redeem one Key and enter that week&apos;s
            Weekly Draw.
          </FaqBody>
        </FaqItem>

        <FaqItem number={4} question="Can I redeem more than one Key per week?">
          <FaqBody>No. You may only redeem one Key per week.</FaqBody>
        </FaqItem>

        <FaqItem
          number={5}
          question="What if I missed this week's redemption window? Can I still enter the Weekly Draw?"
        >
          <FaqBody>
            No. If you don&apos;t redeem a Key during the current Weekly
            Draw&apos;s redemption window (
            <strong>Thursday to Thursday UTC</strong>), you won&apos;t be able
            to participate in that week&apos;s draw.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["key-types-tiers"]}
        onClick={() => toggleSection("key-types-tiers")}
        sectionId="key-types-tiers"
      >
        Key Types and Tiers
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["key-types-tiers"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["key-types-tiers"] ? "none" : "0",
          overflow: expandedSections["key-types-tiers"] ? "visible" : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="What are Key types, and how are they determined?"
        >
          <FaqBody>
            There are five types of Keys: Common, Uncommon, Rare, Epic, and
            Legendary. Your Key tier is based on the eligible quests you
            completed during the Thursday-Thursday cycle, reflecting your
            ongoing engagement.
          </FaqBody>
        </FaqItem>

        <FaqItem number={2} question="What do Key tiers affect?">
          <FaqBody>
            Key tiers determine your placement in the Weekly Draw&apos;s reward
            structure. Higher-tier Keys unlock access to greater portions of the
            prize pool.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["claiming-understanding-rewards"]}
        onClick={() => toggleSection("claiming-understanding-rewards")}
        sectionId="claiming-understanding-rewards"
      >
        Claiming &amp; Understanding Rewards
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["claiming-understanding-rewards"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["claiming-understanding-rewards"]
            ? "none"
            : "0",
          overflow: expandedSections["claiming-understanding-rewards"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem number={1} question="What kind of rewards can I win?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>You may receive:</FaqBody>
            <FaqBody>
              <BulletList
                use={<ul />}
                sx={{
                  "& li": {
                    color: "base.color.text.body.secondary",
                  },
                }}
              >
                {OverallPrizeTypes.map((prizeType) => (
                  <BulletList.Item key={prizeType} size="small">
                    {prizeType}
                  </BulletList.Item>
                ))}
              </BulletList>
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem number={2} question="What's the reward pool for each week?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              The total reward pool is reviewed and updated weekly. It includes
              a mix of rewards such as:
            </FaqBody>
            <BulletList
              use={<ul />}
              sx={{
                "& li": {
                  color: "base.color.text.body.secondary",
                },
              }}
            >
              {OverallPrizeTypes.map((prizeType) => (
                <BulletList.Item key={prizeType} size="small">
                  {prizeType}
                </BulletList.Item>
              ))}
            </BulletList>
            <FaqBody>
              Each week's pool is distributed across <b>Key Tiers</b> based on
              how many <Link href="/quests/#main-quests">eligible quests</Link>{" "}
              you've completed between <strong>{activityPeriod?.from}</strong> -{" "}
              <strong>{activityPeriod?.to}</strong>. Click on the progress bar
              at the top of the screen to reveal the complete list of eligible
              quests.
            </FaqBody>
            <FaqBody>
              Furthermore, a dedicated portion of the pool is reserved for the{" "}
              <b>Mythic Reward</b> draw, which offers IMX-only rewards.
            </FaqBody>
            <FaqBody>
              You can view the current reward breakdown across all Tiers here:{" "}
              <Link
                href="/rewards/tiers"
                onClick={() => {
                  trackLegacyEvent({
                    screen: "FAQ",
                    userJourney: "Rewards",
                    action: "Pressed",
                    control: "ViewTierRewards",
                    controlType: "Link",
                  });
                }}
              >
                View Weekly Draw Tier Rewards
              </Link>
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem number={3} question="When do I need to verify my account?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              You'll need to verify your phone number and link a social media
              account <strong>the first time you win IMX tokens</strong>. This
              is a one-time security measure to protect the integrity of the
              rewards program. Once you've completed verification, you can claim
              all future IMX rewards without needing to verify again. Non-IMX
              rewards (such as Gems and Free Entries) can be claimed without
              verification.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem
          number={4}
          question="How are rewards distributed across Key Tiers?"
        >
          <FaqBody>
            Each Key Tier grants access to a different portion of the Weekly
            Draw reward pool. As your Tier increases, so does your eligibility
            for more exclusive and higher-value rewards - including top prizes
            in the <b>Mythic Reward</b> draw.
          </FaqBody>
          <FaqBody>
            The Weekly Draw is designed to reward ongoing engagement, with
            meaningful rewards available at every Tier.
          </FaqBody>
          <FaqBody>
            You can view the current breakdown of rewards across all Key Tiers
            here:{" "}
            <Link
              href="/rewards/tiers"
              onClick={() => {
                trackLegacyEvent({
                  screen: "FAQ",
                  userJourney: "Rewards",
                  action: "Pressed",
                  control: "ViewTierRewards",
                  controlType: "Link",
                });
              }}
            >
              View Weekly Draw Tier Rewards
            </Link>
          </FaqBody>
          <FaqBody>
            <i>
              Note: Tier distributions are updated each week and may change over
              time.
            </i>
          </FaqBody>
        </FaqItem>

        <FaqItem number={5} question="What is the Mythic Reward draw?">
          <FaqBody>
            Mythic Rewards are a special subset of the weekly prize pool
            composed exclusively of <strong>IMX tokens</strong>.
          </FaqBody>
          <FaqBody>
            Anyone who claims a Key is automatically entered into the Mythic
            Reward draw. Your Key Tier determines which prizes you’re eligible
            to win.
          </FaqBody>

          <FaqBody>
            You can see a full breakdown of the Mythic Reward draw here:{" "}
            <Link
              href="/rewards/tiers"
              onClick={() => {
                trackLegacyEvent({
                  screen: "FAQ",
                  userJourney: "Rewards",
                  action: "Pressed",
                  control: "ViewTierRewards",
                  controlType: "Link",
                });
              }}
            >
              View Weekly Draw Tier Rewards
            </Link>
          </FaqBody>
        </FaqItem>

        <FaqItem number={6} question="When can I claim my rewards?">
          <FaqBody>
            At <strong>1am UTC every Thursday</strong>, rewards from the
            previous week become available to unlock.
          </FaqBody>
        </FaqItem>

        <FaqItem number={7} question="How long do I have to claim my rewards?">
          <FaqBody>
            Rewards expire 30 days after they become available. Make sure to
            claim them before they expire.
          </FaqBody>
        </FaqItem>

        <FaqItem number={8} question="What happens if I win a Free Entry?">
          <FaqBody>
            If you receive a reward that grants a Free Entry, it will be
            automatically applied the next time you redeem Gems in a Weekly
            Draw. You don&apos;t need to take any action.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["verification-anti-fraud"]}
        onClick={() => toggleSection("verification-anti-fraud")}
        sectionId="verification-anti-fraud"
      >
        Verification &amp; Anti-Fraud
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["verification-anti-fraud"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["verification-anti-fraud"] ? "none" : "0",
          overflow: expandedSections["verification-anti-fraud"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="Why must I verify my phone number or link a social media account?"
        >
          <FaqBody>
            We&apos;re dedicated to offering fair, meaningful rewards, so we
            exclude bots and Sybils. Verifying your phone number and linking a
            social media account will help us implement anti-fraud measures and
            strengthen the security of your Passport profile.
          </FaqBody>
        </FaqItem>

        <FaqItem number={2} question='What is a "Sybil" account?'>
          <FaqBody>
            A &quot;Sybil&quot; account is one of many fake or duplicate
            accounts created by the same individual (or group) to appear as
            separate users. These accounts can be used to exploit reward
            programs or otherwise cheat the system. Verifying your account and
            linking trusted identities such as a phone number or social media
            profile will help us eliminate these fraudulent accounts and keep
            the reward process fair for everyone.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["identity-verification"]}
        onClick={() => toggleSection("identity-verification")}
        sectionId="identity-verification"
      >
        Identity Verification
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["identity-verification"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["identity-verification"] ? "none" : "0",
          overflow: expandedSections["identity-verification"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem number={1} question="Why do I need to verify my identity?">
          <FaqBody>
            Once your total rewards reach a certain amount, we'll ask you to
            verify your identity before you can claim additional rewards or
            continue participating in Weekly Draws. This helps us meet
            regulatory requirements and keep the program fair and secure.
          </FaqBody>
        </FaqItem>

        <FaqItem number={2} question="What does identity verification involve?">
          <FaqBody>
            You'll be asked to submit your full name, email address, and a
            screenshot of the wallet address linked to your Immutable Passport.
            You'll also upload a photo of a valid government-issued ID and a
            selfie holding that same ID. In addition, if your ID document does
            not show your current residential address, you will also need to
            submit a copy of a document that shows your name and address, such
            as a phone or utility bill, dated within the last 90 days.
          </FaqBody>
        </FaqItem>

        <FaqItem number={3} question="What documents can I use?">
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>You can submit one of the following:</FaqBody>
            <BulletList
              use={<ul />}
              sx={{
                "& li": {
                  color: "base.color.text.body.secondary",
                },
              }}
            >
              <BulletList.Item size="small">Passport</BulletList.Item>
              <BulletList.Item size="small">
                Driver's Licence (front and back)
              </BulletList.Item>
              <BulletList.Item size="small">National ID card</BulletList.Item>
            </BulletList>
            <FaqBody>
              A selfie with your document is also required. Where your ID
              document, such as a Passport, does not show your current
              residential address, you will also need to submit a copy of a
              document that shows your name and address. Acceptable proof of
              address documents include:
            </FaqBody>
            <BulletList
              use={<ul />}
              sx={{
                "& li": {
                  color: "base.color.text.body.secondary",
                },
              }}
            >
              <BulletList.Item size="small">A phone bill</BulletList.Item>
              <BulletList.Item size="small">
                A utility bill, such as for water, electricity or gas supply
              </BulletList.Item>
              <BulletList.Item size="small">An internet bill</BulletList.Item>
              <BulletList.Item size="small">
                A bank statement (note that financial details should be redacted
                by the user prior to submission)
              </BulletList.Item>
            </BulletList>
            <FaqBody>
              In order to be accepted, these proofs of address must include your
              full name (matching your ID document) and be dated within the last
              90 days.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem number={4} question="How long does it take to get verified?">
          <FaqBody>
            Submitting your information takes only a few minutes. Our team will
            review your submission within thirty business days, and will be in
            touch with you if any additional information is required to complete
            your verification.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={5}
          question="Can I still earn rewards while verification is pending?"
        >
          <FaqBody>
            You can continue completing quests and earning Gems, but you won't
            be able to claim rewards or enter Weekly Draws until your identity
            has been verified.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={6}
          question="What happens if I don't complete verification?"
        >
          <FaqBody>
            You'll be blocked from claiming rewards or entering new draws. Any
            rewards you've already earned will remain on hold until your
            identity is verified.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={7}
          question="My identity verification was unsuccessful. What should I do?"
        >
          <FaqBody>
            Please email{" "}
            <a
              href="mailto:kyc@immutable.com"
              style={{ textDecoration: "underline" }}
            >
              kyc@immutable.com
            </a>{" "}
            with your name, email, and Passport wallet address. Our team will
            help you resolve the issue.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={8}
          question="Will I be told why my submission was unsuccessful?"
        >
          <Stack direction="column" gap="base.spacing.x2">
            <FaqBody>
              If the documents you have submitted are insufficient to complete
              verification or are unclear, we will let you know and allow you to
              submit further documents.
            </FaqBody>
            <FaqBody>
              However, if your verification is unsuccessful for an alternative
              reason, we are generally unable to provide specific rejection
              reasons in line with our obligations. Submissions may be declined
              for several reasons, including unsupported countries, invalid or
              unclear documents, duplicate use of the same ID, or underage
              users.
            </FaqBody>
          </Stack>
        </FaqItem>

        <FaqItem
          number={9}
          question="Will I need to verify my identity again in the future?"
        >
          <FaqBody>
            You won't need to verify your identity each time you claim rewards.
            However, we may request additional information in the future if
            required to meet our compliance obligations.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["phone-verification-errors"]}
        onClick={() => toggleSection("phone-verification-errors")}
        sectionId="phone-verification-errors"
      >
        Common Phone Verification Errors
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["phone-verification-errors"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["phone-verification-errors"]
            ? "none"
            : "0",
          overflow: expandedSections["phone-verification-errors"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question='When I verify my phone number, I get the error "Verification attempts limit reached. Please try again in 24 hours." What should I do to fix this issue?'
        >
          <FaqBody>
            This error happens when you&apos;ve tried to verify your phone
            number too many times. When you encounter this error, you can return
            in 24 hours to try and verify the number again.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={2}
          question='When I verify my phone number, I get the error "We cannot verify phone numbers from your region in compliance with regulations. Some of our services may not be available in your region." What should I do to fix this issue?'
        >
          <FaqBody>
            This error occurs when our verification system flags your phone
            number as coming from a country we cannot serve in compliance with
            regulations.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={3}
          question='When I verify my phone number, I get the error "Our verification system flagged this number and cannot be used. Please contact support if you believe this may be an error." What should I do to fix this issue?'
        >
          <FaqBody>
            This error occurs when our verification system flags your phone
            number, so you cannot use it to verify your account.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={4}
          question='When I verify my phone number, I get the error "Invalid or expired code. Please try again." What should I do to fix this issue?'
        >
          <FaqBody>
            This error occurs when you enter the wrong one-time password or if
            your one-time password has expired. Please double-check the text and
            enter the correct code. If you haven&apos;t received the code in 15
            minutes, choose the &quot;Resend code&quot; option to get a new one.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["social-linking-errors"]}
        onClick={() => toggleSection("social-linking-errors")}
        sectionId="social-linking-errors"
      >
        Common Social Account Linking Errors
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["social-linking-errors"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["social-linking-errors"] ? "none" : "0",
          overflow: expandedSections["social-linking-errors"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question={
            'Why do I see the error "That didn\'t work. Go back to the other tab and try again." when linking my social account?'
          }
        >
          <FaqBody>
            This message indicates an issue on the original &quot;rewards&quot;
            page. To see the specific error, simply return to that tab or
            window. The system will then display a more detailed message
            explaining what went wrong, such as &quot;Account already in
            use&quot; or &quot;Account didn&apos;t match.&quot;
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={2}
          question={
            'What does "Account already in use" mean when linking my social account?'
          }
        >
          <FaqBody>
            You&apos;ll see the message &quot;This account is already connected
            to another Passport. Please use a different X account to
            proceed.&quot; if the social media account is linked to a different
            Passport profile. If this happens, either log in with the original
            Passport that&apos;s already connected or try linking a different
            social media account.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={3}
          question={
            'What does "Account didn\'t match" mean when linking my social account?'
          }
        >
          <FaqBody>
            You&apos;ll see the message &quot;You previously used a different{" "}
            {"{platform}"} account. Make sure you&apos;re using that account to
            proceed.&quot; if you&apos;re attempting to link an account
            different from the one you used before. In other words, you&apos;re
            signing in with a new or alternate handle instead of the one on
            file. Switch to the correct social media handle to complete the
            linking process successfully.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["managing-linked-accounts"]}
        onClick={() => toggleSection("managing-linked-accounts")}
        sectionId="managing-linked-accounts"
      >
        Managing Linked Accounts
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["managing-linked-accounts"]
            ? "visible"
            : "hidden",
          maxHeight: expandedSections["managing-linked-accounts"]
            ? "none"
            : "0",
          overflow: expandedSections["managing-linked-accounts"]
            ? "visible"
            : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="Can I link my phone/social to multiple accounts?"
        >
          <FaqBody>
            No, you cannot. Phone numbers and social accounts can only be tied
            to a single account.
          </FaqBody>
        </FaqItem>

        <FaqItem
          number={2}
          question="Can I unlink my phone/social from the account?"
        >
          <FaqBody>
            Not yet, but we expect to launch a feature in the coming weeks that
            lets you unlink a phone or a social account from your profile.
          </FaqBody>
        </FaqItem>
      </Stack>

      <SectionHeading
        isExpanded={expandedSections["getting-help"]}
        onClick={() => toggleSection("getting-help")}
        sectionId="getting-help"
      >
        Getting Help
      </SectionHeading>
      <Stack
        direction="column"
        gap="base.spacing.x4"
        sx={{
          visibility: expandedSections["getting-help"] ? "visible" : "hidden",
          maxHeight: expandedSections["getting-help"] ? "none" : "0",
          overflow: expandedSections["getting-help"] ? "visible" : "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <FaqItem
          number={1}
          question="How do I get support if I have issues linking accounts or verifying information?"
        >
          <FaqBody>
            If you encounter any problems linking social accounts or verifying
            your phone number, please file a support ticket{" "}
            <Link
              href="https://support.immutable.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </Link>
            .
          </FaqBody>
        </FaqItem>
      </Stack>
    </Stack>
  );
};

export const RedemptionFAQContent = () => {
  const { prizeConfig, activityPeriod, sweepstake } = useWeeklyRewards();
  const mythicPercentage = calculateMythicPercentage(
    Number(sweepstake?.current?.totalIMX || sweepstake?.previous?.totalIMX),
    Number(
      sweepstake?.current?.totalIMXSansJackpot ||
        sweepstake?.previous?.totalIMXSansJackpot,
    ),
  );
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(
    VALID_SECTIONS.reduce(
      (acc, section) => {
        acc[section] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
    // Update the hash when toggling a section
    window.location.hash = section;
  };

  // Handle initial hash on page load
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash && VALID_SECTIONS.includes(hash)) {
      // Expand the target section and let browser handle native scrolling
      setExpandedSections((prev) => ({
        ...prev,
        [hash]: true,
      }));
    }
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        margin: "0 auto",
        padding: "base.spacing.x6",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: "base.spacing.x4",
        }}
      >
        <Box sx={{ mb: "base.spacing.x4" }}>
          <Body sx={{ color: "base.color.text.body.secondary" }}>
            <Link
              href="/rewards"
              onClick={() => {
                // Track click on "Back to Rewards" link
                trackLegacyEvent({
                  screen: "FAQ",
                  userJourney: "Rewards",
                  action: "Pressed",
                  control: "BackToRewards",
                  controlType: "Link",
                });
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: 500,
                  fontSize: "18px",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    marginRight: "8px",
                    fontSize: "18px",
                  }}
                >
                  ←
                </span>
                Back to Rewards
              </div>
            </Link>
          </Body>
        </Box>

        <Heading
          size="large"
          weight="bold"
          sx={{
            mb: "base.spacing.x2",
            color: "base.color.text.heading.primary",
          }}
        >
          FAQs
        </Heading>
      </Box>

      <Divider
        sx={{
          bg: "base.color.translucent.emphasis.100",
          mb: "base.spacing.x4",
        }}
      />
      <SparkSweepstakeFAQContent
        prizeConfig={prizeConfig}
        activityPeriod={activityPeriod}
        mythicPercentage={mythicPercentage}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      />
    </Box>
  );
};
